import classnames from 'classnames'
import { addDays, addMonths, format, isBefore, isSameDay, subDays, subMonths } from 'date-fns'
import { groupBy, sortBy } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import NavigateNextIcon from '@material-symbols/svg-300/rounded/navigate_next.svg'
import NavigateBeforeIcon from '@material-symbols/svg-300/rounded/navigate_before.svg'

import Button from '../../components/basics/Button/Button'
import Menu from '../../components/basics/Menu/Menu'
import learningInvestmentFunnel from '../../state/calculations/learningInvestmentFunnel'
import { useLearningItems } from '../../state/entities'
import useTimeInvested from '../../state/queries/useTimeInvested'
import useUserLearningItems from '../../state/queries/useUserLearningItems'
import useQueryParameters, { queryParameters } from '../../utilities/useQueryParameters'

import styles from './InsightsPage.module.scss'
import InsightsPageLearningInvestmentChart from './InsightsPageLearningInvestmentChart'
import InsightsPageLearningInvestmentFunnel from './InsightsPageLearningInvestmentFunnel'

/**********************************************************************
 * InsightsPageLearningInvestment
 **********************************************************************/
const TIME_RANGES: { [k: string]: any | undefined } = {
  range12months: {
    getStart: (d: string) => format(subMonths(new Date(d), 12), 'yyyy-MM-dd'),
    next: (d: string) => format(addMonths(new Date(d), 12), 'yyyy-MM-dd'),
  },
  range6months: {
    getStart: (d: string) => format(subMonths(new Date(d), 6), 'yyyy-MM-dd'),
    next: (d: string) => format(addMonths(new Date(d), 6), 'yyyy-MM-dd'),
  },
  range1month: {
    getStart: (d: string) => format(subMonths(new Date(d), 1), 'yyyy-MM-dd'),
    next: (d: string) => format(addMonths(new Date(d), 1), 'yyyy-MM-dd'),
  },
  range1week: {
    getStart: (d: string) => format(subDays(new Date(d), 7), 'yyyy-MM-dd'),
    next: (d: string) => format(addDays(new Date(d), 7), 'yyyy-MM-dd'),
  },
}

const timeRangeDef = queryParameters().string('to').string('range')

export const InsightsPageLearningInvestment = React.memo(function InsightsPageLearningInvestment({
  userUuid,
}: {
  userUuid?: string
}) {
  const { t } = useTranslation()

  // time range
  const rangeParameters = useQueryParameters(timeRangeDef)

  const lastDate = rangeParameters.to || format(new Date(), 'yyyy-MM-dd')
  const toDate = format(addDays(new Date(lastDate), 1), 'yyyy-MM-dd')
  const rangeKey = TIME_RANGES[rangeParameters.range] ? rangeParameters.range : 'range6months'
  const rangeType = TIME_RANGES[rangeKey]
  const fromDate = rangeType.getStart(toDate)

  const handlePrevRange = useCallback(
    () =>
      rangeParameters.set((prev) => ({ ...prev, to: format(subDays(new Date(fromDate), 1), 'yyyy-MM-dd') }), {
        preventScrollReset: true,
      }),
    [fromDate, rangeType, rangeParameters.set]
  )

  const handleNextRange = useCallback(
    () => rangeParameters.set((prev) => ({ ...prev, to: rangeType.next(lastDate) }), { preventScrollReset: true }),
    [lastDate, rangeType, rangeParameters.set]
  )

  const handleChangeRange = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      const range = e.currentTarget.dataset.range
      if (range) {
        rangeParameters.set((prev) => ({ ...prev, range }), { preventScrollReset: true })
      }
    },
    [fromDate, rangeType, rangeParameters.set]
  )

  // time investment
  const { data, isLoading } = useTimeInvested({ fromDate, toDate })

  // total
  const total = useMemo(
    () => data.days.flatMap(({ entries }) => entries.map(({ seconds }) => seconds)).reduce((acc, m) => acc + m, 0),
    [data]
  )

  // top courses
  const topCourseUuids = useMemo(
    () =>
      sortBy(
        Object.values(
          groupBy(
            sortBy(
              data.days.flatMap(({ entries }) => entries),
              'uuid'
            ),
            'uuid'
          )
        ).map((entries) => ({
          uuid: entries[0].uuid,
          seconds: entries.reduce((acc, { seconds }) => acc + seconds, 0),
        })),
        'seconds'
      )
        .reverse()
        .slice(0, 3),
    [data]
  )

  const topCourses = useLearningItems(topCourseUuids.map(({ uuid }) => uuid))

  // chart data
  const chartData = useMemo(() => {
    let fromDate = new Date(data.fromDate)
    const toDate = new Date(data.toDate)
    const days = data.days.map((e) => ({ ...e, when: new Date(e.when) }))
    let idx = 0
    while (isBefore(fromDate, toDate)) {
      if (!days[idx] || !isSameDay(fromDate, days[idx].when)) {
        days.splice(idx, 0, {
          when: fromDate,
          entries: [],
          total: 0,
        })
      }
      idx += 1
      fromDate = addDays(fromDate, 1)
    }

    return { fromDate: new Date(data.fromDate), toDate: subDays(new Date(data.toDate), 1), days }
  }, [data])

  // funnelData
  const { userLearningItems } = useUserLearningItems(userUuid)
  const funnelData = useMemo(() => learningInvestmentFunnel(userLearningItems), [userLearningItems])

  return (
    <section className={styles.InsightsPageLearningInvestment}>
      <header>
        <h1>{t('insights.title my learning investment')}</h1>
      </header>

      <main className={classnames({ skeleton: isLoading })}>
        <section className={styles.chart}>
          <div className={styles.rangeNavigation}>
            <Menu small label={t(`insights.time range.${rangeKey}`)}>
              {Object.keys(TIME_RANGES).map((key) => (
                <li key={key}>
                  <Button data-range={key} onClick={handleChangeRange} menuItem>
                    {t(`insights.time range.${key}`)}
                  </Button>
                </li>
              ))}
            </Menu>
          </div>

          <InsightsPageLearningInvestmentChart data={chartData} />

          <div className={styles.dateNavigation}>
            <Button small onClick={handlePrevRange}>
              <NavigateBeforeIcon />
            </Button>
            <span>{t('format.DATE', { date: new Date(fromDate) })}</span>
            {'-'}
            <span>{t('format.DATE', { date: new Date(lastDate) })}</span>
            <Button small onClick={handleNextRange}>
              <NavigateNextIcon />
            </Button>
          </div>
        </section>

        <section className={styles.totalDuration}>
          <Trans t={t} i18nKey="insights.total {minutes} spent from {start} to {end}">
            <span />
            <span />
            <span />
            {{ minutes: Math.round((total || 0) / 60) }}
            {{ start: new Date(fromDate) }}
            {{ end: new Date(lastDate) }}
          </Trans>
        </section>

        <section className={styles.topCourses}>
          <header>
            <h1>{t('insights.title my top courses')}</h1>
          </header>

          <table className={classnames({ skeleton: topCourses.learningItems[0]?.isDummy })}>
            <tbody>
              {topCourses.learningItems.map((learningItem, i) => (
                <tr key={learningItem.uuid}>
                  <td>{learningItem.title}</td>
                  <td>{t('format.DURATION', { duration: { seconds: topCourseUuids[i].seconds } })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className={styles.funnel}>
          <InsightsPageLearningInvestmentFunnel data={funnelData} />
        </section>
      </main>
    </section>
  )
})
